import { ManagerHandlerAction, UIField } from "@bonusmap/api-types";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { templateParseToElement } from "../../logic/templateParse.js";
import { DataSlot } from "./data-slot.js";
import { DataContextComponent } from "./data-context-component.js";
export interface DataComponentTemplateData {
    field: UIField;
    action: ManagerHandlerAction;
    value: any;
    // Additional data can be added as needed extending this interface
}
/**
 * È un componente base per gli elementi web che sono destinati a 
 * costruire una interfaccia per il "view" o "edit" di un elemento di oggetto che viene dal database
 * i templates sono gestiti dall'elemento figlio che estende questo componente
 * qui ci sono solo le funzioni di base per gestire l'elemento
 */
export abstract class DataBaseComponent extends DataContextComponent {
    static webComponentName: string = "data-base";
    protected _templateId: string = "data-base-template"; // No template needed
    constructor() {
        super();
        this.style.display = "contents"; // Use display: contents to avoid extra wrapper
        this.setupGlobalListeners();
    }

    public get dataSlotElement(): DataSlot {
        return this.closest("data-slot") as DataSlot; // Get the closest data-slot element
    }
    get readOnly(): boolean {
        return this.hasAttribute('readonly') || this.hasAttribute('disabled') ||
            this.dataSlotElement?.hasAttribute('readonly') || this.dataSlotElement?.hasAttribute('disabled');
    }
    private _field!: UIField;
    get field(): UIField {
        return this._field;
    }
    set field(field: UIField) {
        this._field = field;
    }
    private _valueInitialized: boolean = false;
    public get valueInitialized(): boolean {
        return this._valueInitialized;
    }
    private set valueInitialized(value: boolean) {
        this._valueInitialized = value;
    }
    private _value: any; // Placeholder for the value of the component
    get value(): any {
        return this._value;
    }
    set value(val: any) {
        if (!this.valueInitialized && this._value) {
            this.onSetValue(val);
            this.valueInitialized = true;
        }
        if (this._value !== val) {
            this.onChangeValue(val);
        }
        this._value = val;
    }
    // Solo quando cambia il valore
    protected onChangeValue(newValue: any): void {
        // console.log(`DataBaseComponent: Value change to`, newValue);
    }
    // Quando viene settato il valore (anche la prima volta)
    protected onSetValue(newValue: any): void {
    }
    protected setupGlobalListeners(): void {
        // eventi globali delegati
    }
    protected setupEventListeners(): void {
        // This method can be overridden by subclasses to set up event listeners
    }
    protected processInputValue(value: any): any {
        return value;
    }
    protected syncHandler = (event: Event): void => {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        if (!target) return;
        let newValue: any;
        if (target instanceof HTMLInputElement) {
            const type = (target.type || '').toLowerCase();
            if (type === 'checkbox') {
                newValue = target.checked;
            } else if (type === 'number') {
                const v = target.value.trim();
                newValue = v === '' ? null : Number(v);
            } else {
                newValue = target.value;
            }
        } else {
            newValue = (target as HTMLTextAreaElement | HTMLSelectElement).value;
        }
        // console.log(`DataBaseComponent: Sync value from input`, newValue);
        this.value = this.processInputValue(newValue);
        this.dispatch('form-changed', { value: this.value });
    }

    private _inputSelector!: string;
    // il selettore per l'input, se non specificato viene generato in base al nome del field
    protected get inputSelector(): string {
        if(!this._inputSelector && this.field && this.field.name) {
            this._inputSelector = `[name="${this.field.name}"]`; // Select by name attribute
        }
        return this._inputSelector;
    }

    protected setupSyncEventListeners(): void {
        if(!this.field || !this.field?.name) return;
        const selector = this.inputSelector;
        if(!selector) return;
        const el = this.querySelector(selector);
        if(!el) return;
        this.on('input', selector, this.syncHandler.bind(this));
        this.on('change', selector, this.syncHandler.bind(this));
    }
    abstract addDataForTemplate(): Record<string, any>; // costringe subclasses to implement this method
    private getDataForTemplate(): Record<string, any> {
        const data: DataComponentTemplateData = {
            field: this.field,
            action: this.action,
            value: this.value
            // Additional data can be added as needed
        };
        const additionalData = this.addDataForTemplate();
        return { ...data, ...additionalData };
    }
    /**
     * @returns void
     * This method is called to render the component.
     * Non dovrebbe essere esteso se non in casi di template specifici.
     */
    protected render(): void {
        if (!this.field) {
            console.warn("DataBaseComponent: Field is not set, cannot render.");
            return;
        }
        templateParseToElement(this.templateId, this, this.getDataForTemplate());
        this.afterRender();
        this.setupEventListeners();
        this.setupSyncEventListeners();
        
        this.dispatch('component-rendered', { field: this.field, value: this.value });
    }

}